import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Eye, Trash2, LayoutGrid, Layers } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import DriverRolodex from '../components/DriverRolodex';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('rolodex'); // 'rolodex' or 'grid'
  const [newDriver, setNewDriver] = useState({ name: '', employee_id: '', hire_date: '', status: 'active', shift: '', employment_type: '' });
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCurrentUser();
    fetchDrivers();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, getAuthHeaders());
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user');
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/drivers`, getAuthHeaders());
      setDrivers(response.data);
    } catch (error) {
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDriver = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newDriver,
        hire_date: newDriver.hire_date ? new Date(newDriver.hire_date).toISOString() : null,
        shift: newDriver.shift || null,
        employment_type: newDriver.employment_type || null,
      };
      await axios.post(`${API}/drivers`, payload, getAuthHeaders());
      toast.success('Driver added successfully');
      
      // Close dialog and reset form
      setIsDialogOpen(false);
      setTimeout(() => {
        setNewDriver({ name: '', employee_id: '', hire_date: '', status: 'active', shift: '', employment_type: '' });
      }, 300);
      
      fetchDrivers();
    } catch (error) {
      toast.error('Failed to add driver');
    }
  };

  const handleDeleteDriver = async (driverId) => {
    if (!window.confirm('Are you sure you want to delete this driver?')) return;
    try {
      await axios.delete(`${API}/drivers/${driverId}`, getAuthHeaders());
      toast.success('Driver deleted successfully');
      fetchDrivers();
    } catch (error) {
      toast.error('Failed to delete driver');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading drivers...</div>;
  }

  const filteredDrivers = drivers.filter(driver => 
    driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (driver.employee_id && driver.employee_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-8" data-testid="drivers-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2">Drivers</h1>
          <p className="text-slate-600 dark:text-slate-400">Manage your driver roster</p>
        </div>
        {(currentUser?.role === 'admin' || currentUser?.role === 'coach') && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
            <Button data-testid="add-driver-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Driver
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-driver-dialog">
            <DialogHeader>
              <DialogTitle>Add New Driver</DialogTitle>
              <DialogDescription>
                Add a new driver to the system with their basic information.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddDriver} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  data-testid="driver-name-input"
                  value={newDriver.name}
                  onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="employee_id">Employee ID</Label>
                <Input
                  id="employee_id"
                  data-testid="driver-employee-id-input"
                  value={newDriver.employee_id}
                  onChange={(e) => setNewDriver({ ...newDriver, employee_id: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hire_date">Hire Date</Label>
                <Input
                  id="hire_date"
                  data-testid="driver-hire-date-input"
                  type="date"
                  value={newDriver.hire_date}
                  onChange={(e) => setNewDriver({ ...newDriver, hire_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  data-testid="driver-status-select"
                  className="w-full px-3 py-2 border rounded-md"
                  value={newDriver.status}
                  onChange={(e) => setNewDriver({ ...newDriver, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="shift">Shift</Label>
                  <select
                    id="shift"
                    data-testid="driver-shift-select"
                    className="w-full px-3 py-2 border rounded-md"
                    value={newDriver.shift}
                    onChange={(e) => setNewDriver({ ...newDriver, shift: e.target.value })}
                  >
                    <option value="">Not set</option>
                    <option value="Early">Early</option>
                    <option value="Late">Late</option>
                    <option value="Night">Night</option>
                    <option value="Part-Time">Part-Time</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="employment_type">Employment Type</Label>
                  <select
                    id="employment_type"
                    data-testid="driver-employment-type-select"
                    className="w-full px-3 py-2 border rounded-md"
                    value={newDriver.employment_type}
                    onChange={(e) => setNewDriver({ ...newDriver, employment_type: e.target.value })}
                  >
                    <option value="">Not set</option>
                    <option value="Permanent">Permanent</option>
                    <option value="Agency">Agency</option>
                  </select>
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-driver-button">
                Add Driver
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Input
              placeholder="Search by name or employee ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
              data-testid="driver-search-input"
            />
            <p className="text-sm text-slate-600">
              Showing {filteredDrivers.length} of {drivers.length} drivers
            </p>
          </div>
          
          <div className="flex items-center space-x-2 no-print">
            <Button
              variant={viewMode === 'rolodex' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('rolodex')}
              data-testid="rolodex-view-button"
            >
              <Layers className="h-4 w-4 mr-2" />
              Rolodex
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
              data-testid="grid-view-button"
            >
              <LayoutGrid className="h-4 w-4 mr-2" />
              Grid
            </Button>
          </div>
        </div>
      </div>

      {viewMode === 'rolodex' ? (
        <DriverRolodex 
          drivers={filteredDrivers} 
          onDelete={handleDeleteDriver}
          currentUser={currentUser}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDrivers.map((driver) => (
          <Card key={driver.id} className="hover:shadow-lg transition-shadow" data-testid={`driver-card-${driver.id}`}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{driver.name}</span>
                <span className={`text-xs px-2 py-1 rounded ${driver.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                  {driver.status}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                {driver.employee_id && (
                  <p className="text-slate-600">
                    <span className="font-medium">ID:</span> {driver.employee_id}
                  </p>
                )}
                {driver.hire_date && (
                  <p className="text-slate-600">
                    <span className="font-medium">Hired:</span> {format(new Date(driver.hire_date), 'MMM d, yyyy')}
                  </p>
                )}
              </div>
              <div className="flex space-x-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => navigate(`/drivers/${driver.id}`)}
                  data-testid={`view-driver-${driver.id}`}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </Button>
                {currentUser?.role === 'admin' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteDriver(driver.id)}
                    data-testid={`delete-driver-${driver.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        </div>
      )}

      {filteredDrivers.length === 0 && drivers.length > 0 && (
        <div className="text-center py-12" data-testid="no-results-message">
          <p className="text-slate-500">No drivers match your search.</p>
        </div>
      )}

      {drivers.length === 0 && (
        <div className="text-center py-12" data-testid="no-drivers-message">
          <p className="text-slate-500">No drivers found. Add your first driver to get started.</p>
        </div>
      )}
    </div>
  );
}